import { useEffect, useState, type ReactNode } from "react";
import {
  OPEN_COOKIE_SETTINGS_EVENT,
  acceptAll,
  getConsent,
  needsConsent,
  rejectAll,
  setConsent,
} from "../lib/consent";
import { useNav } from "../context/NavContext";
import { Button } from "./ui";
import { Ic } from "./icons";

/* Bannière consentement cookies — conforme CNIL.
 *
 * Deux modes :
 *  - "banner"     : premier affichage — 3 boutons équivalents Tout refuser /
 *                   Personnaliser / Tout accepter (exigence CNIL : refus aussi
 *                   accessible et visible qu'acceptation, en un clic).
 *  - "customize"  : détail par catégorie avec toggles. Réutilisé pour
 *                   "Gérer mes cookies" depuis un footer.
 *
 * Apparaît automatiquement au premier chargement si `needsConsent()` (pas de
 * choix stocké OU stocké depuis plus de 6 mois). L'utilisateur doit faire un
 * choix ; on n'affiche PAS de croix en mode "banner" tant qu'il n'a rien
 * sélectionné (le choix ne peut pas être éludé implicitement).
 *
 * Le mode "customize" peut être ouvert à tout moment via le helper
 * `openCookieSettings()` (event bus), branché sur tous les footers légaux.
 */

export function CookieBanner() {
  const { go } = useNav();
  const [visible, setVisible] = useState(false);
  const [mode, setMode] = useState<"banner" | "customize">("banner");
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  // Premier chargement : affiche la bannière si aucun choix valide n'est
  // stocké. Volontairement après le mount pour éviter un flash pendant le
  // bootstrap Firebase.
  useEffect(() => {
    setVisible(needsConsent());
  }, []);

  // Ouverture programmatique depuis un lien "Gérer mes cookies".
  useEffect(() => {
    function open() {
      const current = getConsent();
      setAnalytics(current?.analytics ?? false);
      setMarketing(current?.marketing ?? false);
      setMode("customize");
      setVisible(true);
    }
    window.addEventListener(OPEN_COOKIE_SETTINGS_EVENT, open);
    return () => window.removeEventListener(OPEN_COOKIE_SETTINGS_EVENT, open);
  }, []);

  if (!visible) return null;

  // Croix de fermeture dispo uniquement si un choix a déjà été stocké (mode
  // "Gérer mes cookies" réouvert). Au premier affichage — même en passant par
  // "Personnaliser" — on force l'utilisateur à trancher, pour ne pas éluder
  // le choix de manière implicite.
  const canDismiss = !needsConsent();

  function handleAccept() {
    acceptAll();
    setVisible(false);
  }

  function handleReject() {
    rejectAll();
    setVisible(false);
  }

  function handleCustomSave() {
    setConsent({ analytics, marketing });
    setVisible(false);
  }

  function openPolicy() {
    window.location.hash = "#cookies";
    go("legal");
    setVisible(false);
  }

  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-labelledby="cookie-banner-title"
      className="fixed bottom-3 left-3 right-3 sm:bottom-6 sm:left-6 sm:right-auto sm:w-[460px] z-[100] bg-paper rounded-2xl shadow-pop border border-ink-200/70 p-5 sm:p-6"
    >
      {mode === "banner" && (
        <>
          <div className="flex items-center gap-2 mb-3">
            <Ic.Shield size={13} className="text-ink-700" />
            <div className="font-mono text-[10.5px] uppercase tracking-[0.14em] text-ink-500">
              Vos cookies
            </div>
          </div>
          <h2
            id="cookie-banner-title"
            className="text-[17px] sm:text-[18px] font-semibold tracking-tight text-ink-900"
          >
            Vous avez le choix.
          </h2>
          <p className="mt-2 text-[13px] text-ink-600 leading-relaxed">
            Arpentia utilise des cookies strictement nécessaires au
            fonctionnement du service, et — avec votre accord — des cookies de
            mesure d'audience (Google Analytics). Votre choix est conservé 6
            mois et modifiable à tout moment.{" "}
            <button
              onClick={openPolicy}
              className="underline decoration-ink-300 underline-offset-2 hover:text-ink-900"
            >
              En savoir plus
            </button>
            .
          </p>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-2">
            <Button variant="soft" size="sm" onClick={handleReject}>
              Tout refuser
            </Button>
            <Button
              variant="soft"
              size="sm"
              onClick={() => setMode("customize")}
            >
              Personnaliser
            </Button>
            <Button variant="soft" size="sm" onClick={handleAccept}>
              Tout accepter
            </Button>
          </div>
        </>
      )}

      {mode === "customize" && (
        <>
          <div className="flex items-center justify-between mb-3 gap-4">
            <h2
              id="cookie-banner-title"
              className="text-[17px] sm:text-[18px] font-semibold tracking-tight text-ink-900"
            >
              Personnaliser mes cookies
            </h2>
            {canDismiss && (
              <button
                onClick={() => setVisible(false)}
                aria-label="Fermer"
                className="w-8 h-8 rounded-full hover:bg-ink-100 grid place-items-center text-ink-500 shrink-0"
              >
                <CloseIcon />
              </button>
            )}
          </div>
          <div className="mt-1 space-y-3">
            <CategoryRow
              label="Strictement nécessaires"
              desc="Authentification (Firebase Auth) et protection anti-fraude (App Check / reCAPTCHA)."
              required
            />
            <CategoryRow
              label="Mesure d'audience"
              desc="Google Analytics 4 (Firebase Analytics) — comprendre comment le site est utilisé pour l'améliorer."
              value={analytics}
              onChange={setAnalytics}
            />
            <CategoryRow
              label="Marketing & reciblage"
              desc="Aucun cookie marketing n'est actuellement déposé. Cette catégorie est prévue pour un usage futur éventuel."
              value={marketing}
              onChange={setMarketing}
              disabled
            />
          </div>
          <div className="mt-4 grid grid-cols-2 gap-2">
            <Button variant="soft" size="sm" onClick={handleReject}>
              Tout refuser
            </Button>
            <Button variant="soft" size="sm" onClick={handleCustomSave}>
              Enregistrer mes choix
            </Button>
          </div>
          <button
            onClick={openPolicy}
            className="mt-3 text-[11.5px] text-ink-500 hover:text-ink-800 underline decoration-ink-300 underline-offset-2"
          >
            Voir la politique cookies complète
          </button>
        </>
      )}
    </div>
  );
}

function CategoryRow({
  label,
  desc,
  value,
  onChange,
  required,
  disabled,
}: {
  label: string;
  desc: ReactNode;
  value?: boolean;
  onChange?: (v: boolean) => void;
  required?: boolean;
  disabled?: boolean;
}) {
  const checked = required ? true : value ?? false;
  const isDisabled = required || disabled;
  return (
    <div className="flex items-start gap-3 p-3 rounded-lg bg-ink-50 hairline">
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={label}
        onClick={() => !isDisabled && onChange?.(!checked)}
        disabled={isDisabled}
        className={`mt-0.5 shrink-0 w-9 h-5 rounded-full relative transition ${
          checked ? "bg-ink-900" : "bg-ink-300"
        } ${isDisabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
      >
        <span
          className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform ${
            checked ? "translate-x-4" : ""
          }`}
        />
      </button>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <div className="text-[13px] font-medium text-ink-900">{label}</div>
          {required && (
            <span className="font-mono text-[9.5px] uppercase tracking-widest text-ink-500">
              Toujours actif
            </span>
          )}
        </div>
          <div className="mt-0.5 text-[12px] text-ink-600 leading-relaxed">
            {desc}
          </div>
      </div>
    </div>
  );
}

function CloseIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M18 6 6 18" />
      <path d="m6 6 12 12" />
    </svg>
  );
}
